import { useEffect, useRef, useState } from "react";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import Input from "../../components/common/Input";
import menuService from "../../services/menuService";
import useFormKeyboardNavigation from "../../hooks/useFormKeyboardNavigation";
import alertService from "../../utils/alertService";

function MenuListPage() {
  const formRef = useRef(null);
  const [menus, setMenus] = useState([]);
  const [tree, setTree] = useState([]);
  const [menuName, setMenuName] = useState("");
  const [actionName, setActionName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  useFormKeyboardNavigation(formRef, `${isLoading}:${menuName}:${actionName}:${isActive}`);

  const loadMenus = async () => {
    try {
      setIsLoading(true);
      const [list, treeList] = await Promise.all([menuService.getMenus(), menuService.getMenuTree()]);
      setMenus(Array.isArray(list) ? list : []);
      setTree(Array.isArray(treeList) ? treeList : []);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to load menus");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMenus();
  }, []);

  const createMenu = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      await menuService.createMenu({ menuName, actionName, isActive });
      setMenuName("");
      setActionName("");
      setIsActive(true);
      await loadMenus();
    } catch (err) {
      setError(err.message || "Failed to create menu");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteMenu = async (menu) => {
    const result = await alertService.deleteConfirm(menu?.menuName || "this menu");
    if (!result?.isConfirmed) return;

    try {
      setIsLoading(true);
      await menuService.deleteMenu(menu.id);
      await loadMenus();
      alertService.success("Menu deleted successfully.");
    } catch (err) {
      setError(err.message || "Failed to delete menu");
      alertService.error(err.message || "Failed to delete menu");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Menus</h1>
      {error ? <ErrorAlert message={error} /> : null}
      <form
        ref={formRef}
        className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
        onSubmit={createMenu}
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Input
            label="Menu Name"
            name="menuName"
            value={menuName}
            onChange={(e) => setMenuName(e.target.value)}
          />
          <Input
            label="Action Name"
            name="actionName"
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
          />
        </div>

        <div className="mt-6 flex justify-start">
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-600/20"
            />
            <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
              Is Active
            </span>
          </label>
        </div>

        <div className="mt-6 flex justify-start">
          <Button type="submit" isLoading={isLoading}>
            Create Menu
          </Button>
        </div>
      </form>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <h2 className="mb-4 text-lg font-semibold">All Menus</h2>
          <ul className="space-y-2">
            {menus.map((menu) => (
              <li key={menu.id} className="flex items-center justify-between border-b border-slate-100 py-2 dark:border-slate-800">
                <span>{menu.menuName}</span>
                <Button variant="ghost" onClick={() => deleteMenu(menu)}>Delete</Button>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <h2 className="mb-4 text-lg font-semibold">Menu Tree</h2>
          <ul className="space-y-2 text-sm">
            {tree.map((menu) => (
              <li key={menu.id}>
                {menu.menuName}
                {Array.isArray(menu.children) && menu.children.length > 0 ? (
                  <ul className="ml-4 mt-1 space-y-1 text-slate-600 dark:text-slate-300">
                    {menu.children.map((child) => <li key={child.id}>- {child.menuName}</li>)}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default MenuListPage;
